import {inject, Injectable} from "@angular/core";
import {InfoRepo} from "../repositories/api/info.repo";
import {filter, map, retry, timeout} from "rxjs/operators";
import {InfoDto} from "../repositories/dto/info.dto";
import {BehaviorSubject, distinctUntilChanged, fromEvent, interval, MonoTypeOperatorFunction, Observable, throwError} from "rxjs";
import {HttpErrorResponse, HttpResponse} from "@angular/common/http";
import {environment} from "../../environments/environment";
import {isDefined, isUuid, notDefined} from "../libs/utils/epic.utils";
import {OrgRepo} from "../repositories/api/org.repo";
import {VoidDto} from "../repositories/dto/void.dto";
import {WrapperDto} from "../repositories/dto/wrapper.dto";
import {OrgService} from "./org.service";
import {RepoProvider} from "../repositories/repo.provider";
import {LoggingService} from "./logger/logging.service";

// do not use "AbstractService" => Circular dependency
@Injectable({providedIn: 'root'})
export class InfoService {

  // A local tick decides whether a ping is due - it costs no request
  private static readonly TICK_MS: number = 1000;
  // Core unreachable: retry after 5 s, doubling up to 30 s, with +-20 % jitter so returning tabs do not ping in step
  private static readonly RETRY_MIN_MS: number = 5000;
  private static readonly RETRY_MAX_MS: number = 30000;
  // A ping without an answer counts as a failure, otherwise it would hold back every later one
  private static readonly PING_TIMEOUT_MS: number = 10000;

  private readonly orgService = inject(OrgService);
  private readonly orgRepo: OrgRepo = inject(RepoProvider).getRepo(OrgRepo);
  private readonly infoRepo: InfoRepo = inject(RepoProvider).getRepo(InfoRepo);
  private readonly log = inject(LoggingService);
  private readonly pingEnabled = environment.api.ping.enabled;
  private readonly pingInitial = environment.api.ping.initial;
  private readonly pingInterval = environment.api.ping.interval;
  private readonly pingError = new BehaviorSubject<boolean>(false);
  private readonly info = new BehaviorSubject<InfoDto | undefined>(undefined);
  private orgId: string | undefined;
  private infoLoading = false;
  private continuousErrors: number | undefined; // undefined = initial call
  private nextPingAt: number = 0;
  private pingIdle: boolean = true;

  constructor() {
    if (this.pingEnabled) {
      this.orgService.getCurrentOrg().subscribe((org) => {
        this.orgId = isDefined(org) && isUuid(org.id) ? org.id : undefined;
      });
      this.startPing();
    }
  }

  public getInfo(): Observable<InfoDto> {
    if (!this.infoLoading) {
      this.infoLoading = true;
      this.infoRepo.getInfo().pipe(
        retry({delay: 1000}), // Repeat indefinitely with a 1s delay
        map((w) => w.getFirstData())
      ).subscribe((res) => this.info.next(res));
    }
    return this.info.asObservable().pipe(filter(i => isDefined(i)));
  }

  // noinspection JSUnusedGlobalSymbols
  public getPingError(): Observable<boolean> {
    return this.pingError.asObservable().pipe(distinctUntilChanged());
  }

  private startPing(): void {
    this.nextPingAt = Date.now() + this.pingInitial;
    interval(InfoService.TICK_MS).pipe(filter(() => Date.now() >= this.nextPingAt)).subscribe(() => this.execPing());
    // The browser knows at once when the network goes and comes back; the interval alone takes up to two rounds
    fromEvent(window, 'offline').subscribe(() => this.pingError.next(true));
    fromEvent(window, 'online').subscribe(() => this.execPing());
    // Browsers throttle timers in background tabs - check at once when the tab is visible again
    fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible')).subscribe(() => this.execPing());
  }

  // Without network a ping only fails in the browser, and one running ping is enough
  private execPing(): void {
    if (navigator.onLine && this.pingIdle) {
      this.pingIdle = false;
      this.sendPing();
    }
  }

  private sendPing(): void {
    if (isDefined(this.orgId)) {
      this.orgRepo.ping(this.orgId).pipe(this.withTimeout()).subscribe({
        next: (w: WrapperDto<VoidDto>) => {
          if (w.status === 200) {
            this.setReachable();
          } else {
            this.handleError();
          }
        },
        error: (err: HttpErrorResponse) => this.handlePingFailure(err)
      });
    } else {
      this.infoRepo.ping().pipe(this.withTimeout()).subscribe({
        next: (response: HttpResponse<any>) => {
          if (response?.status === 204) {
            this.setReachable();
          } else {
            this.handleError();
          }
        },
        error: (err: HttpErrorResponse) => this.handlePingFailure(err)
      });
    }
  }

  private withTimeout<T>(): MonoTypeOperatorFunction<T> {
    return timeout({first: InfoService.PING_TIMEOUT_MS, with: () => throwError(() => new HttpErrorResponse({status: 0, statusText: 'Unknown Error'}))});
  }

  // A 4xx is the server's own answer, so the connection stands - an expired login or a missing permission is handled elsewhere
  private handlePingFailure(err: HttpErrorResponse): void {
    if (err.status === 0 || err.status >= 500) {
      this.handleError(err);
    } else {
      this.setReachable();
    }
  }

  private setReachable(): void {
    this.continuousErrors = 0;
    this.pingError.next(false);
    this.finishPing(this.pingInterval);
  }

  // The first ping counts at once, every later one only on the second failure in a row
  private handleError(err?: HttpErrorResponse): void {
    const initial: boolean = notDefined(this.continuousErrors);
    this.continuousErrors = (this.continuousErrors ?? 0) + 1;
    if (initial || this.continuousErrors >= 2) {
      this.markLost(err);
    }
    this.finishPing(this.buildRetryDelay());
  }

  // One error entry per outage, not one per failed ping
  private markLost(err?: HttpErrorResponse): void {
    if (isDefined(err) && this.isReachable()) {
      this.log.error(err.message);
    }
    this.pingError.next(true);
  }

  private isReachable(): boolean {
    return this.pingError.getValue() === false;
  }

  private finishPing(delay: number): void {
    this.pingIdle = true;
    this.nextPingAt = Date.now() + delay;
  }

  // Background tabs keep the normal rhythm, only a visible tab retries sooner
  private buildRetryDelay(): number {
    let delay: number = this.pingInterval;
    if (document.visibilityState === 'visible') {
      const base: number = Math.min(InfoService.RETRY_MAX_MS, InfoService.RETRY_MIN_MS * 2 ** ((this.continuousErrors ?? 1) - 1));
      delay = Math.round(base * (0.8 + Math.random() * 0.4));
    }
    return delay;
  }
}
